import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { 
  Users, UserPlus, Search, Loader2, Key, UserX, Shield, User, 
  Mail, Phone, MoreHorizontal, CheckCircle, XCircle, X, ChevronRight,
  Copy, MessageSquare
} from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useToast } from '../../hooks/use-toast';

const ROLE_CONFIG = {
  guard: { 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'from-blue-500 to-blue-600',
    label: 'Guard',
    icon: Shield
  },
  supervisor: { 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    bgColor: 'from-purple-500 to-purple-600',
    label: 'Supervisor',
    icon: Users
  },
  admin: { 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    bgColor: 'from-amber-500 to-amber-600',
    label: 'Admin',
    icon: Shield
  },
  super_admin: { 
    color: 'bg-red-100 text-red-700 border-red-200',
    bgColor: 'from-red-500 to-red-600',
    label: 'Super Admin',
    icon: Shield
  },
};

// User Card Component
const UserCard = ({ user, onSelect, onResetPassword, onDeactivate }) => {
  const role = ROLE_CONFIG[user.role] || ROLE_CONFIG.guard;
  
  return (
    <div 
      className="group p-4 rounded-xl border border-slate-200 bg-white hover:shadow-lg transition-all cursor-pointer"
      onClick={() => onSelect(user)}
      data-testid={`user-card-${user.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${role.bgColor} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
          {user.name?.[0]?.toUpperCase() || '?'}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-800 truncate">{user.name}</h3>
            {!user.is_active && (
              <Badge variant="outline" className="bg-slate-100 text-slate-500 text-[10px]">
                Inactive
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate flex items-center gap-1">
            <Mail className="w-3 h-3" /> {user.email}
          </p>
          <Badge variant="outline" className={`mt-2 ${role.color}`}>
            {role.label}
          </Badge>
        </div>
        
        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onResetPassword(user); }}>
              <Key className="w-4 h-4 mr-2" /> Reset Password
            </DropdownMenuItem>
            {user.phone && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/${user.phone}`, '_blank'); }}>
                <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {user.is_active && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDeactivate(user); }} className="text-red-600">
                <UserX className="w-4 h-4 mr-2" /> Deactivate
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default function AdminUsers() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [addingRole, setAddingRole] = useState('guard');
  const [submitting, setSubmitting] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', phone: '' });
  const [createdPassword, setCreatedPassword] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const loadUsers = async () => {
    try {
      const data = await api.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filtered users
  const filteredUsers = useMemo(() => {
    let data = users;
    
    if (search) {
      data = data.filter(u => 
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.phone?.includes(search)
      );
    }
    
    return data;
  }, [users, search]);

  const guards = filteredUsers.filter(u => u.role === 'guard');
  const supervisors = filteredUsers.filter(u => u.role === 'supervisor');

  // Stats
  const stats = useMemo(() => ({
    total: users.length,
    guards: users.filter(u => u.role === 'guard').length,
    supervisors: users.filter(u => u.role === 'supervisor').length,
    active: users.filter(u => u.is_active).length,
  }), [users]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email) {
      toast({ variant: "destructive", title: "Error", description: "Name and email are required" });
      return;
    }

    setSubmitting(true);
    try {
      let result;
      if (addingRole === 'guard') {
        result = await api.createGuard(newUser);
      } else {
        result = await api.createSupervisor(newUser);
      }
      setCreatedPassword(result.generated_password);
      setNewUser({ name: '', email: '', phone: '' });
      loadUsers();
      toast({ title: "User Created", description: `${addingRole} created successfully` });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
      setShowAddSheet(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const result = await api.resetUserPassword(user.id);
      setSelectedUser({ ...user, newPassword: result.generated_password });
      toast({ title: "Password Reset", description: "New password generated" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    }
  };

  const handleDeactivate = async () => {
    if (!confirmDeactivate) return;
    try {
      await api.deactivateUser(confirmDeactivate.id);
      loadUsers();
      toast({ title: "User Deactivated", description: `${confirmDeactivate.name} has been deactivated` });
      setConfirmDeactivate(null);
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Copied to clipboard" });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading users...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Team Members</h1>
            <p className="text-slate-500 text-sm">Manage guards and supervisors</p>
          </div>
          <Button 
            onClick={() => setShowAddSheet(true)} 
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4" /> Add User
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Users className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats.guards}</p>
                  <p className="text-xs text-blue-600">Guards</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{stats.supervisors}</p>
                  <p className="text-xs text-purple-600">Supervisors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats.active}</p>
                  <p className="text-xs text-green-600">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="user-search-input"
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Quick filter chips */}
              {filteredUsers.length <= 15 && filteredUsers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filteredUsers.slice(0, 8).map((user) => {
                    const role = ROLE_CONFIG[user.role];
                    return (
                      <button
                        key={user.id}
                        onClick={() => setSearch(user.name)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 transition-colors"
                      >
                        <div className={`w-5 h-5 rounded-md bg-gradient-to-br ${role.bgColor} flex items-center justify-center text-white text-[10px] font-bold`}>
                          {user.name?.[0]?.toUpperCase()}
                        </div>
                        <span className="text-slate-700">{user.name?.split(' ')[0]}</span>
                      </button>
                    );
                  })}
                  {filteredUsers.length > 8 && (
                    <span className="px-2.5 py-1 text-sm text-slate-400">
                      +{filteredUsers.length - 8} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" className="gap-2">
              All <Badge variant="secondary" className="ml-1">{filteredUsers.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="guards" className="gap-2">
              Guards <Badge variant="secondary" className="ml-1">{guards.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="supervisors" className="gap-2">
              Supervisors <Badge variant="secondary" className="ml-1">{supervisors.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <UserGrid 
              users={filteredUsers} 
              onSelect={setSelectedUser}
              onResetPassword={handleResetPassword}
              onDeactivate={setConfirmDeactivate}
            />
          </TabsContent>
          <TabsContent value="guards" className="mt-4">
            <UserGrid 
              users={guards} 
              onSelect={setSelectedUser}
              onResetPassword={handleResetPassword}
              onDeactivate={setConfirmDeactivate}
            />
          </TabsContent>
          <TabsContent value="supervisors" className="mt-4">
            <UserGrid 
              users={supervisors} 
              onSelect={setSelectedUser}
              onResetPassword={handleResetPassword}
              onDeactivate={setConfirmDeactivate}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add User Sheet */}
      <Sheet open={showAddSheet} onOpenChange={(open) => {
        setShowAddSheet(open);
        if (!open) {
          setCreatedPassword('');
          setNewUser({ name: '', email: '', phone: '' });
        }
      }}>
        <SheetContent className="sm:max-w-lg bg-white">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-slate-900">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add New User
            </SheetTitle>
            <SheetDescription className="text-slate-500">
              Create a new guard or supervisor account
            </SheetDescription>
          </SheetHeader>
          
          {createdPassword ? (
            <div className="space-y-6 mt-6">
              <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-3">
                  <CheckCircle className="w-5 h-5" />
                  User created successfully!
                </div>
                <p className="text-sm text-green-600 mb-2">Generated password:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-green-100 rounded-lg font-mono text-lg font-bold text-green-800">
                    {createdPassword}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(createdPassword)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Share this password with the user. They can log in with their email and this password.
              </p>
              <Button onClick={() => { setShowAddSheet(false); setCreatedPassword(''); }} className="w-full">
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleAddUser} className="space-y-6 mt-6">
              {/* Role Selection */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Role</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAddingRole('guard')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      addingRole === 'guard' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${ROLE_CONFIG.guard.bgColor} flex items-center justify-center mx-auto mb-2`}>
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-medium text-slate-800">Guard</p>
                    <p className="text-xs text-slate-500">Field worker</p>
                  </button>
                  {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && (
                    <button
                      type="button"
                      onClick={() => setAddingRole('supervisor')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        addingRole === 'supervisor' 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${ROLE_CONFIG.supervisor.bgColor} flex items-center justify-center mx-auto mb-2`}>
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <p className="font-medium text-slate-800">Supervisor</p>
                      <p className="text-xs text-slate-500">Team manager</p>
                    </button>
                  )}
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Full Name *</Label>
                <Input
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  className="h-11"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Email *</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  className="h-11"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Phone (Optional)</Label>
                <Input
                  placeholder="+1234567890"
                  value={newUser.phone}
                  onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                  className="h-11"
                />
                <p className="text-xs text-slate-500">Include country code for WhatsApp</p>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddSheet(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create User
                </Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>

      {/* User Detail Sheet */}
      <Sheet open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${ROLE_CONFIG[selectedUser?.role]?.bgColor || 'from-slate-500 to-slate-600'} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                {selectedUser?.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <SheetTitle>{selectedUser?.name}</SheetTitle>
                <Badge variant="outline" className={ROLE_CONFIG[selectedUser?.role]?.color}>
                  {ROLE_CONFIG[selectedUser?.role]?.label}
                </Badge>
              </div>
            </div>
          </SheetHeader>
          
          <div className="space-y-4 mt-6">
            {/* Email */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">{selectedUser?.email}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(selectedUser?.email)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            {/* Phone */}
            {selectedUser?.phone && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{selectedUser?.phone}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => window.open(`https://wa.me/${selectedUser?.phone}`, '_blank')}
                  className="text-green-600"
                >
                  <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                </Button>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-500">Status</span>
              <Badge variant="outline" className={selectedUser?.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                {selectedUser?.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {/* Created */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-500">Created</span>
              <span className="text-slate-700">
                {selectedUser?.created_at ? format(new Date(selectedUser.created_at), 'MMM d, yyyy') : '—'}
              </span>
            </div>

            {/* New Password (if just reset) */}
            {selectedUser?.newPassword && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-700 font-medium mb-2">New Password</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-amber-100 rounded-lg font-mono font-bold text-amber-800">
                    {selectedUser.newPassword}
                  </code>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(selectedUser.newPassword)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="pt-4 space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => handleResetPassword(selectedUser)}
              >
                <Key className="w-4 h-4 mr-2" /> Reset Password
              </Button>
              {selectedUser?.is_active && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-red-600 hover:bg-red-50"
                  onClick={() => { setSelectedUser(null); setConfirmDeactivate(selectedUser); }}
                >
                  <UserX className="w-4 h-4 mr-2" /> Deactivate User
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Deactivate Confirmation */}
      <AlertDialog open={!!confirmDeactivate} onOpenChange={(open) => !open && setConfirmDeactivate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{confirmDeactivate?.name}</strong>? 
              They will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-red-600 hover:bg-red-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

// User Grid Component
function UserGrid({ users, onSelect, onResetPassword, onDeactivate }) {
  if (users.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-slate-200" />
          <p className="text-lg font-medium text-slate-600">No users found</p>
          <p className="text-sm text-slate-400 mt-1">Try a different search term</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {users.map((user) => (
        <UserCard
          key={user.id}
          user={user}
          onSelect={onSelect}
          onResetPassword={onResetPassword}
          onDeactivate={onDeactivate}
        />
      ))}
    </div>
  );
}
